// src/components/Auth/SignupSuccess.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://http://https://southamerica-east1-drecenna.cloudfunctions.net/api';

const SignupSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [pendingAccountData, setPendingAccountData] = useState(null);
  const [successAnimation, setSuccessAnimation] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  
  // Extrair o ID da sessão da URL
  const sessionId = new URLSearchParams(location.search).get('session_id');
  
  useEffect(() => {
    // Ativar animação após o componente ser montado
    setTimeout(() => {
      setSuccessAnimation(true);
    }, 300);

    if (!sessionId) {
      setError('Sessão inválida.');
      setLoading(false);
      return;
    }

    const verifySession = async () => {
      try {
        // Se for recuperação, verificar informações na conta pendente
        if (sessionId === 'recover') {
          setLoading(true);
          // Precisamos de um email para procurar na conta pendente
          // Como não temos, mostramos um formulário de email primeiro
          setLoading(false);
          return;
        }
        
        // Verificar sessão no Stripe
        try {
          const response = await axios.get(`${API_URL}/checkout-session?sessionId=${sessionId}`);
          const session = response.data;
          
          // Verificar se o pagamento foi concluído com sucesso
          if (session.payment_status !== 'paid') {
            setError('O pagamento ainda não foi confirmado. Por favor, aguarde ou entre em contato com o suporte.');
            setLoading(false);
            return;
          }

          setSessionInfo(session);
          
          // Preencher o email automaticamente
          setFormData(prev => ({
            ...prev,
            email: session.customer_email
          }));
          
          setLoading(false);
        } catch (stripeError) {
          console.error('Erro ao verificar sessão no Stripe:', stripeError);
          
          // Se não conseguimos verificar no Stripe, vamos verificar nas contas pendentes
          try {
            // Precisamos perguntar o email para o usuário primeiro já que não temos como saber
            setLoading(false);
            return;
          } catch (pendingError) {
            console.error('Erro ao verificar conta pendente:', pendingError);
            setError('Não foi possível verificar o pagamento. Por favor, tente novamente ou entre em contato com o suporte.');
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar sessão:', error);
        setError('Não foi possível verificar o pagamento. Por favor, tente novamente ou entre em contato com o suporte.');
        setLoading(false);
      }
    };

    verifySession();
  }, [sessionId]);

  // Se tivermos apenas o sessionId 'recover', precisamos do email para procurar na conta pendente
  const handleFindPendingAccount = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setFormErrors({...formErrors, email: 'Email é obrigatório'});
      return;
    }
    
    setLoading(true);
    
    try {
      // Verificar se existe uma conta pendente para este email
      const response = await axios.post(`${API_URL}/check-payments-by-email`, { 
        email: formData.email 
      });
      
      if (response.data.found) {
        // Se for um usuário que já tem conta
        if (response.data.alreadyRegistered) {
          navigate('/login', { 
            state: { 
              message: response.data.message || 'Você já possui uma conta registrada. Por favor, faça login.' 
            } 
          });
          return;
        }
        
        // Encontrou informações - preencher formulário
        setPendingAccountData(response.data);
        setLoading(false);
      } else {
        setError('Não foi possível encontrar um pagamento para este email. Por favor, verifique se está usando o mesmo email usado durante o checkout.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao buscar conta pendente:', error);
      setError('Erro ao verificar sua conta. Por favor, tente novamente ou entre em contato com o suporte.');
      setLoading(false);
    }
  };

  // Função para formatar número de telefone
  const formatPhoneNumber = (value) => {
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a formatação (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar formatação específica para o campo de telefone
    if (name === 'phoneNumber') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpar erro do campo quando o usuário começa a digitar
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    
    // Validação de telefone: deve ter pelo menos 10 dígitos após remover não-números
    const phoneDigits = formData.phoneNumber.replace(/\D/g, '');
    if (!phoneDigits) {
      errors.phoneNumber = 'Telefone é obrigatório';
    } else if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      errors.phoneNumber = 'Telefone inválido. Use o formato (XX) XXXXX-XXXX';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email inválido';
    }
    
    if (!formData.password) {
      errors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      errors.password = 'A senha deve ter pelo menos 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar formulário
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Enviar dados para criar a conta
      const response = await axios.post(`${API_URL}/create-account`, {
        name: formData.name,
        phoneNumber: formData.phoneNumber.replace(/\D/g, ''), // Enviar apenas os dígitos
        email: formData.email,
        password: formData.password,
        sessionId: sessionId
      });
      
      if (response.data.success) {
        // Redirecionar para o login com mensagem de sucesso
        navigate('/login', { 
          state: { 
            message: 'Conta criada com sucesso! Você já pode fazer login.' 
          } 
        });
      }
    } catch (error) {
      console.error('Erro ao criar conta:', error);
      
      // Verificar se o erro é de "email já em uso"
      if (error.response?.data?.userExists) {
        // Usuário já existe e a entrada pendingAccounts já foi removida pelo backend
        // Redirecionar para o login com mensagem informativa
        navigate('/login', {
          state: {
            message: error.response.data.message || 'Um usuário com este email já existe. Por favor, faça login.'
          }
        });
        return;
      }
      
      setError(error.response?.data?.error || 'Erro ao criar conta. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Renderizar formulário de email para buscar conta pendente
  if (sessionId === 'recover' && !pendingAccountData && !loading) {
    return (
      <div className="auth-container">
        <div className={`auth-card ${successAnimation ? 'animate-fade-in' : ''}`}>
          <div className="card-header">
            <h2>Recuperar Dados de Pagamento</h2>
            <p>Por favor, informe o email que você utilizou durante o pagamento:</p>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleFindPendingAccount} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">
                <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                Email
              </label>
              <div className="input-container">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input-with-icon ${formErrors.email ? 'error' : ''}`}
                  placeholder="seu.email@exemplo.com"
                />
              </div>
              {formErrors.email && <div className="error-text">{formErrors.email}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">
                <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                Telefone
              </label>
              <div className="input-container">
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  className={`input-with-icon ${formErrors.phoneNumber ? 'error' : ''}`}
                />
              </div>
              <small className="help-text">Opcional: Informe seu telefone para contato</small>
              {formErrors.phoneNumber && <div className="error-text">{formErrors.phoneNumber}</div>}
            </div>
            
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>Buscar Dados de Pagamento</span>
                </>
              )}
            </button>
          </form>
          
          <div className="auth-links">
            <Link to="/recover-subscription" className="auth-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Voltar para recuperação
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card loading-card">
          <div className="loading-spinner"></div>
          <p>Verificando seu pagamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className={`auth-card ${successAnimation ? 'animate-fade-in' : ''}`}>
          <div className="card-header">
            <h2>Erro</h2>
          </div>
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/pricing')} className="secondary-button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Voltar para Planos
          </button>
          <div className="auth-links">
            <Link to="/recover-subscription" className="auth-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              Tentar recuperar assinatura
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Mapear nomes dos planos
  const planNames = {
    personal: 'Pessoal',
    business: 'Empresarial',
    business_pro: 'Empresarial Pro'
  };

  // Determinar o tipo de plano da sessão ou da conta pendente
  const planType = pendingAccountData 
    ? pendingAccountData.subscription?.planType 
    : sessionInfo?.metadata?.planType;

  return (
    <div className="auth-container">
      <div className={`auth-card success-card ${successAnimation ? 'animate-fade-in' : ''}`}>
        <div className="success-icon-container">
          <svg xmlns="http://www.w3.org/2000/svg" className="success-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
        
        <h2>Pagamento Confirmado!</h2>
        <p className="success-message">
          Seu pagamento para o plano <strong>{planNames[planType] || 'Selecionado'}</strong> foi confirmado com sucesso!
        </p>
        <p>Complete seu cadastro para começar a usar sua conta.</p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Nome da Empresa
            </label>
            <div className="input-container">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-with-icon ${formErrors.name ? 'error' : ''}`}
                placeholder="Nome da sua empresa"
              />
            </div>
            {formErrors.name && <div className="error-text">{formErrors.name}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="phoneNumber">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              Telefone
            </label>
            <div className="input-container">
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                className={`input-with-icon ${formErrors.phoneNumber ? 'error' : ''}`}
              />
            </div>
            {formErrors.phoneNumber && <div className="error-text">{formErrors.phoneNumber}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email
            </label>
            <div className="input-container">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={sessionInfo?.customer_email || pendingAccountData}
                className={`input-with-icon ${formErrors.email ? 'error' : ''}`}
                placeholder="seu.email@exemplo.com"
              />
            </div>
            {formErrors.email && <div className="error-text">{formErrors.email}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              Senha
            </label>
            <div className="input-container">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`input-with-icon ${formErrors.password ? 'error' : ''}`}
                placeholder="Crie uma senha segura"
              />
            </div>
            {formErrors.password && <div className="error-text">{formErrors.password}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                <line x1="12" y1="16" x2="12" y2="16.01"></line>
              </svg>
              Confirmar Senha
            </label>
            <div className="input-container">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`input-with-icon ${formErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirme sua senha"
              />
            </div>
            {formErrors.confirmPassword && <div className="error-text">{formErrors.confirmPassword}</div>}
          </div>
          
          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner"></span>
                <span>Criando Conta...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
                <span>Finalizar Cadastro</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignupSuccess;